/**
 * 儀表板與財務報表的快取（依年度，R40）：畫面先顯示上次的資料、進頁面背景重抓；從沒抓過才顯示骨架
 * 分錄、發票、銀行異動後由頁面訂閱 useDataChanged 重抓（R28）
 */
import { createKeyedCache } from '@redpanda/utils';
import { getDashboard, getTrialBalance, getIncomeStatement, getBalanceSheet, getTaxReport, getComparison, ComparisonQuery } from '../services/api';

export interface DashboardData {
  year: number;
  income: number;
  expense: number;
  netIncome: number;
  monthly: Array<{ month: string; income: number; expense: number }>;
  receivable: { amount: number; count: number };
  overdue: { amount: number; count: number };
  draftJournals: number;
  unreconciledTransactions: number;
  hasCurrentFiscalYear: boolean;
  latestPostedYear: number | null;
  bankAccounts: Array<{ id: string; bankName: string; accountName: string; accountNumberLast4: string; currency: string; balance: number }>;
}

export interface ReportLine { id: string; code: string; name: string; amount: number }

export interface IncomeStatement {
  year: number;
  income: ReportLine[];
  expenses: ReportLine[];
  totalIncome: number;
  totalExpenses: number;
  netIncome: number;
}

export interface BalanceSheet {
  year: number;
  asOf: string;
  assets: ReportLine[];
  liabilities: ReportLine[];
  equity: ReportLine[];
  retainedEarnings: number;
  totalAssets: number;
  totalLiabilities: number;
  totalEquity: number;
  balanced: boolean;
}

export interface TrialBalance {
  year: number;
  asOf: string;
  rows: Array<{ id: string; code: string; name: string; type: string; debit: number; credit: number }>;
  totalDebit: number;
  totalCredit: number;
  balanced: boolean;
}

export const useDashboardData = createKeyedCache('accounting-dashboard', async (q: { year: number }) => (await getDashboard(q.year)).data as DashboardData);
export const useIncomeStatement = createKeyedCache('accounting-report-income', async (q: { year: number }) => (await getIncomeStatement(q.year)).data as IncomeStatement);
export const useBalanceSheet = createKeyedCache('accounting-report-balance', async (q: { year: number }) => (await getBalanceSheet(q.year)).data as BalanceSheet);
export const useTrialBalance = createKeyedCache('accounting-report-trial', async (q: { year: number }) => (await getTrialBalance(q.year)).data as TrialBalance);

/** 年度選項：今年往前 5 年 */
export const yearOptions = (count = 5) => {
  const current = new Date().getFullYear();
  return Array.from({ length: count }, (_, index) => current - index);
};

export interface TaxReport {
  period: string;
  startDate: string;
  endDate: string;
  sales: { invoiceCount: number; taxableAmount: number; zeroRatedAmount: number; outputTax: number; creditNotes: { count: number; amount: number; tax: number } };
  purchases: {
    hasInputAccount: boolean;
    inputTax: number;
    entries: Array<{ id: string; date: string; reference: string | null; description: string; inputTax: number }>;
  };
  netTax: number;
}

export const useTaxReport = createKeyedCache('accounting-tax-report-v2', async (q: { period: string }) => (await getTaxReport(q.period)).data as TaxReport);

/** 營業稅申報期（兩個月一期，以第一個月表示 YYYY-MM），最近 12 期、新到舊 */
export const taxPeriodOptions = () => {
  const now = new Date();
  const firstMonth = now.getMonth() % 2 === 0 ? now.getMonth() : now.getMonth() - 1; // 0-based：1、3、5…月
  return Array.from({ length: 12 }, (_, index) => {
    const date = new Date(now.getFullYear(), firstMonth - index * 2, 1);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
  });
};

export interface ComparisonRow { id: string; code: string; name: string; type: string; amounts: number[] }
export interface Comparison {
  type: 'income' | 'balance';
  unit: 'month' | 'quarter' | 'year';
  periods: Array<{ key: string; startDate: string; endDate: string }>;
  sections: Array<{ type: string; rows: ComparisonRow[]; totals: number[] }>;
  retainedEarnings?: number[];
  netIncome?: number[];
}

export const useComparison = createKeyedCache('accounting-report-comparison', async (q: ComparisonQuery) => (await getComparison(q)).data as Comparison);
